/**
 * "Individual Flags" accordion (Combined Report.png bottom): one row per
 * member with red/yellow counts; rows with findings expand to the member's
 * flag lists (derived server-side — COMBINED-V2-DATA-MAP §2). Built on the
 * shared MemberAccordion, which the Additional Documents / Applicant
 * Consent accordions reuse.
 */
import { FlagTriangleRight } from 'lucide-react'
import type { RiskFlagLists } from '#app/utils/application-detail-v2/report-display'
import type { GroupMember } from '#app/utils/application-detail-v2/types'
import { cn } from '#app/utils/misc'
import { IconSquare } from '../primitives'
import { RiskFlagPanelsBody } from '../sections/RiskFlags'
import { MemberAccordion } from './MemberAccordion'

function FlagCount({
	count,
	tone,
	label,
}: {
	count: number
	tone: 'red' | 'amber'
	label: string
}) {
	return (
		<span
			className={cn(
				'flex items-center gap-1 text-sm font-medium',
				tone === 'red' ? 'text-[#DC2634]' : 'text-[#F59F0A]',
			)}
		>
			<FlagTriangleRight aria-hidden="true" className="size-3.5" />
			{count} {label}
		</span>
	)
}

export function IndividualFlags({
	members,
	memberFlags,
}: {
	members: GroupMember[]
	memberFlags: Record<string, RiskFlagLists>
}) {
	return (
		<MemberAccordion
			title="Individual Flags"
			icon={
				<IconSquare tone="purple" className="bg-[#F1EBF7]">
					<img
						src="/img/individual-flags-icon.svg"
						alt=""
						aria-hidden="true"
						className="size-5"
					/>
				</IconSquare>
			}
			members={members}
			row={(member) => {
				const flags = memberFlags[member.applicationId] ?? {
					red: [],
					yellow: [],
				}
				const total = flags.red.length + flags.yellow.length
				return {
					expandable: total > 0,
					summary:
						total === 0 ? (
							<span className="text-sm text-[#747474]">No flags</span>
						) : (
							<>
								<FlagCount count={flags.red.length} tone="red" label="Red" />
								<FlagCount
									count={flags.yellow.length}
									tone="amber"
									label="Yellow"
								/>
							</>
						),
					body: (
						<RiskFlagPanelsBody
							red={flags.red}
							yellow={flags.yellow}
							className="pb-4"
						/>
					),
				}
			}}
		/>
	)
}
